import { FormattedMessage } from 'react-intl'

import { type PimProductV11 } from '@redteclab/api/clients/bully'

import { useProductPageContext } from '../productPageContext'

import { type ProductDescriptionSectionType } from './ProductDescriptionSection'

type PartialProductDescriptionSectionType = Partial<
  Pick<
    ProductDescriptionSectionType,
    'content' | 'showCautions' | 'expandedByDefault'
  >
> &
  Pick<ProductDescriptionSectionType, 'header'>

const hasContent = (
  section: PartialProductDescriptionSectionType,
): section is ProductDescriptionSectionType => Boolean(section.content)

const descriptionLongIfapSections = (
  product: PimProductV11,
): PartialProductDescriptionSectionType[] => [
  {
    content: product.ifap?.indication,
    header: <FormattedMessage id="product.description.indication" />,
  },
  {
    content: product.ifap?.contraindication,
    expandedByDefault: true,
    header: <FormattedMessage id="product.description.contraindication" />,
  },
  {
    content: product.ifap?.dosage,
    header: <FormattedMessage id="product.description.dosage" />,
  },
  {
    content: product.ifap?.sideeffects,
    expandedByDefault: true,
    header: <FormattedMessage id="product.description.sideeffects" />,
  },
  {
    content: product.ifap?.patientsdetails,
    header: <FormattedMessage id="product.description.patientsdetails" />,
    showCautions: true,
  },
  {
    content: product.ifap?.pregnancy,
    header: <FormattedMessage id="product.description.pregnancy" />,
  },
  {
    content: product.ifap?.modality,
    header: <FormattedMessage id="product.description.modality" />,
  },
  {
    content: product.ifap?.interaction,
    header: <FormattedMessage id="product.description.interaction" />,
  },
  {
    content: product.ifap?.miscellaneous,
    header: <FormattedMessage id="product.description.miscellaneous" />,
  },
]

// eslint-disable-next-line max-lines-per-function -- the rule was disabled before a comment became required
const additionalInformationSections = (
  product: PimProductV11,
): PartialProductDescriptionSectionType[] => [
  {
    content: product.product_details,
    header: (
      <FormattedMessage id="product.details.additionalInfo.productDetails" />
    ),
  },
  {
    content: product.author_portrait,
    header: (
      <FormattedMessage id="product.details.additionalInfo.authorPortrait" />
    ),
  },
  {
    content: product.usp_key_facts,
    header: (
      <FormattedMessage id="product.details.additionalInfo.uspKeyFacts" />
    ),
  },
  {
    content: product.usage,
    header: <FormattedMessage id="product.details.additionalInfo.usage" />,
  },
  {
    content: product.side_effects,
    expandedByDefault: true,
    header: (
      <FormattedMessage id="product.details.additionalInfo.sideEffects" />
    ),
  },
  {
    content: product.active_ingredients,
    header: (
      <FormattedMessage id="product.details.additionalInfo.activeIngredients" />
    ),
  },
  {
    content: product.material_composition,
    header: (
      <FormattedMessage id="product.details.additionalInfo.materialComposition" />
    ),
  },
  {
    content: product.care_instructions,
    header: (
      <FormattedMessage id="product.details.additionalInfo.careInstructions" />
    ),
  },
  {
    content: product.feeding_recommendation,
    header: (
      <FormattedMessage id="product.details.additionalInfo.feedingRecommendation" />
    ),
  },
  {
    content: product.ingredients,
    header: (
      <FormattedMessage id="product.details.additionalInfo.ingredients" />
    ),
  },
  {
    content: product.composition,
    header: (
      <FormattedMessage id="product.details.additionalInfo.composition" />
    ),
  },
  {
    content: product.food_ingredients,
    header: (
      <FormattedMessage id="product.details.additionalInfo.foodIngredients" />
    ),
  },
  {
    content: product.nutritional_information,
    header: (
      <FormattedMessage id="product.details.additionalInfo.nutritionalInformation" />
    ),
  },
  {
    content: product.recommended_intake,
    header: (
      <FormattedMessage id="product.details.additionalInfo.recommendedIntake" />
    ),
  },
  {
    content: product.guaranteed_analysis,
    header: (
      <FormattedMessage id="product.details.additionalInfo.guaranteedAnalysis" />
    ),
  },
  {
    content: product.additives_kg,
    header: (
      <FormattedMessage id="product.details.additionalInfo.additivesKg" />
    ),
  },
  {
    content: product.storage_usage_info,
    header: (
      <FormattedMessage id="product.details.additionalInfo.storageUsageInfo" />
    ),
  },
  {
    content: product.netfilling_quantity,
    header: (
      <FormattedMessage id="product.details.additionalInfo.netfilling_quantity" />
    ),
  },
  {
    content: product.manufacturer_details,
    header: (
      <FormattedMessage id="product.details.additionalInfo.manufacturerDetails" />
    ),
  },
  {
    content: product.animal_food_type,
    header: (
      <FormattedMessage id="product.details.additionalInfo.animalFoodType" />
    ),
  },
]

const descriptionLongSections = (
  product: PimProductV11,
): PartialProductDescriptionSectionType[] => [
  {
    content: product.description_long,
    header: undefined,
  },
]

interface ProductDescriptionSections {
  additionalInformation: ProductDescriptionSectionType[] | null
  descriptionLong: ProductDescriptionSectionType[] | null
  descriptionLongIfap: ProductDescriptionSectionType[] | null
  hasIfapSections: boolean
}

export const useProductDescriptionSections = (): ProductDescriptionSections => {
  const { product } = useProductPageContext()

  const descriptionLongIfap = descriptionLongIfapSections(product)
  const additionalInformation = additionalInformationSections(product)
  const descriptionLong = descriptionLongSections(product)

  const hasIfapSections =
    (product.ifap?.active && descriptionLongIfap.length > 0) ?? false

  return {
    additionalInformation:
      additionalInformation.length > 0
        ? additionalInformation.filter(hasContent)
        : null,
    descriptionLong:
      descriptionLong.length > 0 ? descriptionLong.filter(hasContent) : null,
    descriptionLongIfap:
      descriptionLongIfap.length > 0
        ? descriptionLongIfap.filter(hasContent)
        : null,
    hasIfapSections,
  }
}
